import { defineComponent as _defineComponent } from 'vue'
import * as validation from 'JS/components/blain-form/validation';
import BSwitch from 'JS/components/blain-form/BSwitch.vue';
import bugsnagClient from 'JS/global/bugsnag';
import GlobalData from 'JS/global/window-data';
import HorizontalTextDivider from 'JS/components/HorizontalTextDivider.vue';
import Icon from 'JS/components/Icon.vue';
import LocationService from 'JS/services/LocationService';
import ProductService, { IStockStatusRequest, ProductEffectiveInventoryResolutionType, StoreVariantResponseType } from 'JS/services/ProductService';
import Spinner from 'JS/components/Spinner.vue';
import StoreInfoCard from './StoreInfoCard.vue';
import TransitionExpand from 'JS/components/transitions/TransitionExpand.vue';
import UiButton from 'JS/components/UiButton.vue';
import { BForm, BSubmitButton } from 'JS/components/blain-form';
import { BTextField } from 'JS/components/blain-form';
import { ChangeStoreVariant, StoreChangedEvent } from './change-store-types';
import { computed, onMounted, ref } from 'vue';
import { delay } from 'JS/utilities/promise';
import { FormatOptions } from 'JS/components/blain-form/formatting';
import { ISetLocationResponse } from 'JS/types/Location';
import { IStoreWithStock } from 'JS/types/Inventory';
import { stringIsNullOrWhiteSpace } from 'JS/helpers/functions';



export default /*#__PURE__*/_defineComponent({
  __name: 'ChangeStoreSideMenuContents',
  props: {
    variant: null,
    currentZip: null,
    blainNumber: null,
    desiredQty: null,
    marketRequirement: null
  },
  emits: ["store-changed"],
  setup(__props: any, { emit }: { emit: ({
	(event: 'store-changed', e: StoreChangedEvent): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	variant?: ChangeStoreVariant,
	currentZip?: string,
	blainNumber?: string,
	desiredQty?: number,
	marketRequirement?: string,
}

//Imports




const showInStockOnly = ref(false);
const isLoading = ref(false);
const storeVariant = ref<ChangeStoreVariant>();
const zipCode = ref(props.currentZip);
const zipError = ref(false);
const locationError = ref<"permission-denied" | "general-error" | null>(null);
const userGeoLocation = ref<GeolocationPosition>();
const productSvc = new ProductService();
const locationSvc = new LocationService();

const storeStatusList = ref<IStoreWithStock[]>(null);

const zipFormat: FormatOptions = {
	type: 'numeric',
	length: 5,
};

const stores = computed(() => ({
	current: storeStatusList.value?.find(s => s.store.storeNum === GlobalData.localStore?.storeNum),
	others: storeStatusList.value?.filter(s => s.store.storeNum !== GlobalData.localStore?.storeNum),
}));

const googleApiKey = computed(() => {
	return GlobalData.googleApiKey;
});

function handleZipSearch() {
	userGeoLocation.value = null;
	getStoreStatus(true);
}

function handleSwitchClick(value: boolean) {
	showInStockOnly.value = value;
	getStoreStatus();
}

async function handleUseLocation() {
	isLoading.value = true;
	const promise = new Promise<GeolocationPosition | GeolocationPositionError>((resolve)=> {
		navigator.geolocation.getCurrentPosition(resolve, resolve, { timeout: 10000 });
	});

	const res = await delay(promise, 400);

	if (res instanceof GeolocationPosition) {
		userGeoLocation.value = res;
		getStoreStatus();
	} else {
		//https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError
		isLoading.value = false;
		if (res.code === 1) {
			locationError.value = "permission-denied";
		} else {
			locationError.value = "general-error";
		}
	}
}

function clearError() {
	if (zipCode.value.length === 5) {
		zipError.value = false;
	}
}

async function updateStore(storeNum: number) {
	isLoading.value = true;
	const response: ISetLocationResponse = await locationSvc.setStore(storeNum);
	if (response) {
		let reloadPrevented = false;

		emit('store-changed', {
			preventDefault() {
				reloadPrevented = true;
			}
		});

		if (!reloadPrevented) location.reload();
	}
}

async function getStoreStatus(useZip: boolean = false) {
	isLoading.value = true;

	const shared = {
		zip: zipCode.value,
		mustIncludeLocalStore: true,
		exactPosition: useZip ? null : (userGeoLocation.value ? { latitude: userGeoLocation.value.coords.latitude, longitude: userGeoLocation.value.coords.longitude } : null),
	};

	const req = props.blainNumber
		? productSvc.getStockStatus({
			...shared,
			blainNumber: props.blainNumber,
			filterOnlyInStock: showInStockOnly.value,
		})
		: productSvc.getStoreStatus(shared);

	const response = await delay(req, 400);

	locationError.value = null;
	zipError.value = false;

	switch (response?.resolutionType) {
		case ProductEffectiveInventoryResolutionType.GeoLocationNotFound:
			break;
		case ProductEffectiveInventoryResolutionType.InvalidZip:
			zipError.value = true;
			break;
		case ProductEffectiveInventoryResolutionType.InvalidCoordinates:
			userGeoLocation.value = null;
			locationError.value = 'general-error';
			break;
		case ProductEffectiveInventoryResolutionType.NotFound:
			storeStatusList.value = [];
			break;
		case ProductEffectiveInventoryResolutionType.InvalidBlainNumber:
			bugsnagClient.notify(new Error('Blain number: ' + props.blainNumber + ' was invalid when looking for a store stock'));
			break;
		case ProductEffectiveInventoryResolutionType.Success:
			storeStatusList.value = response.statusList ? response.statusList : [];
			if (useZip) userGeoLocation.value = null;

			if (props.variant) {
				storeVariant.value = props.variant;
			} else {
				switch (response.storeVariant) {
					case StoreVariantResponseType.Tire:
						storeVariant.value = 'tire';
						break;
					case StoreVariantResponseType.Auto:
						storeVariant.value = 'auto';
						break;
					default:
						storeVariant.value = 'store';
				}
			}
			break;
		default :
			bugsnagClient.notify(new Error('Failed to respond to store stock status request'));
	}

	isLoading.value = false;
}

onMounted(()=> {
	getStoreStatus();
});


return { __sfc: true,props, emit, showInStockOnly, isLoading, storeVariant, zipCode, zipError, locationError, userGeoLocation, productSvc, locationSvc, storeStatusList, zipFormat, stores, googleApiKey, handleZipSearch, handleSwitchClick, handleUseLocation, clearError, updateStore, getStoreStatus, validation, BSwitch, HorizontalTextDivider, Icon, Spinner, StoreInfoCard, TransitionExpand, UiButton, BForm, BSubmitButton, BTextField, stringIsNullOrWhiteSpace }
}

})